import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { Table, TableRow, TableCell, TableHeader } from '@tiptap/extension-table';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { createLowlight } from 'lowlight';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from '@mui/material';
import { Close, YouTube, Instagram } from '@mui/icons-material';
import BubbleToolbar from './BubbleToolbar';
import FloatingInsertMenu from './FloatingInsertMenu';
import { VideoEmbed, detectVideoFromUrl, getEmbedSrc } from './VideoEmbed';
import styles from './RichTextEditor.module.scss';
import { DESTINATIONS } from '../../../../utils/common/constants';

const lowlight = createLowlight();

/**
 * RichTextEditor — Tiptap 3.x based Word-like blog content editor.
 */
export default function RichTextEditor({
  content = null,
  onChange,
  uploadToS3,
  beginUpload,
  finishUpload,
}) {
  // ── Image dialog state ─────────────────────────────────────────────────────
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageAlt, setImageAlt] = useState('');
  const imageFileRef = useRef(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [imagePendingFile, setImagePendingFile] = useState(null);

  // ── Video embed dialog state ───────────────────────────────────────────────
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [videoTypeHint, setVideoTypeHint] = useState(null); // 'youtube' | 'instagram'
  const [videoUrl, setVideoUrl] = useState('');
  const [detectedVideo, setDetectedVideo] = useState(null); // { videoType, videoId }

  // ── Link dialog state ──────────────────────────────────────────────────────
  // Dialog is a body-level MUI portal — completely independent of editor focus.
  // The input is intentionally UNCONTROLLED (ref, not state) so React batching
  // never blocks the user from typing.
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkDefaultHref, setLinkDefaultHref] = useState('');
  const linkInputRef = useRef(null);         // reads value directly from the DOM
  const savedSelectionRef = useRef(null);    // ProseMirror selection saved before dialog opens

  // Toolbar position state
  const [bubblePos, setBubblePos] = useState(null); // { top, left }
  const [floatPos, setFloatPos] = useState(null);   // { top, left }
  const editorWrapRef = useRef(null);

  // When true, the next content prop change was caused by the editor itself
  // (user typing / formatting) — skip calling setContent() to avoid cursor reset.
  const skipNextSyncRef = useRef(false);

  // True while BubbleToolbar has an open sub-popover (Link or Heading picker).
  // Prevents the blur timer from hiding the toolbar while the user is
  // interacting with those popovers.
  const toolbarEngagedRef = useRef(false);
  const blurTimerRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
      }),
      CodeBlockLowlight.configure({ lowlight }),
      Image.configure({ inline: false, allowBase64: true }),
      Link.configure({ openOnClick: false, autolink: true }),
      Table.configure({ resizable: false }),
      TableRow,
      TableCell,
      TableHeader,
      Placeholder.configure({
        placeholder: 'Start writing your blog content here… Click the + button on empty lines to insert headings, images, tables or videos. Select text to format it.',
      }),
      Typography,
      VideoEmbed,
    ],
    content: content || { type: 'doc', content: [{ type: 'paragraph' }] },
    onUpdate: ({ editor: ed }) => {
      // Mark this as an internal change so the sync effect won't call
      // setContent() and reset the cursor.
      skipNextSyncRef.current = true;
      onChange?.(ed.getJSON());
    },
    onSelectionUpdate: ({ editor: ed }) => {
      updateToolbarPositions(ed);
    },
    onFocus: ({ editor: ed }) => {
      updateToolbarPositions(ed);
    },
    onBlur: () => {
      // Clear any existing timer first
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
      // Use a generous delay so toolbar button / popover clicks can register
      // before we hide. If a sub-popover is open (toolbarEngaged) don't hide.
      blurTimerRef.current = setTimeout(() => {
        if (!toolbarEngagedRef.current) {
          setBubblePos(null);
          setFloatPos(null);
        }
      }, 350);
    },
  });

  // Calculate and update positions for both toolbars
  const updateToolbarPositions = useCallback((ed) => {
    if (!ed || !editorWrapRef.current) return;

    // A sub-popover (Link input or Heading picker) is open — leave the toolbar
    // exactly where it is. Updating positions here would call setBubblePos(null)
    // and destroy the popover before the user finishes interacting with it.
    if (toolbarEngagedRef.current) return;
    const { state, view } = ed;
    const { selection } = state;
    const { from, to, empty } = selection;
    const wrapRect = editorWrapRef.current.getBoundingClientRect();

    // ── Bubble Menu ──────────────────────────────────────────────────────
    // Show when:
    //   (a) user has selected text (any amount, including multi-word), OR
    //   (b) cursor is sitting inside a link — so auto-detected links and
    //       manually applied links are always editable by clicking into them.
    const hasTextSelection = !empty && !ed.isActive('image');
    const cursorInsideLink  = empty && ed.isActive('link');

    if (hasTextSelection || cursorInsideLink) {
      try {
        let toolbarLeft, toolbarTop;

        if (hasTextSelection) {
          // Centre the toolbar horizontally above the selection
          const startCoords = view.coordsAtPos(from);
          const endCoords   = view.coordsAtPos(to);
          toolbarLeft = (startCoords.left + endCoords.left) / 2;
          toolbarTop  = Math.min(startCoords.top, endCoords.top);
        } else {
          // Cursor-in-link: anchor the toolbar just above the cursor
          const coords = view.coordsAtPos(from);
          toolbarLeft  = coords.left;
          toolbarTop   = coords.top;
        }

        setBubblePos({
          top:  toolbarTop  - wrapRect.top - 44,   // 44 px above anchor
          left: toolbarLeft - wrapRect.left,
        });
      } catch {
        setBubblePos(null);
      }
    } else {
      setBubblePos(null);
    }

    // ── Floating Insert Menu: show on empty paragraph (but not in a link) ──
    const isEmptyParagraph =
      empty &&
      !ed.isActive('link') &&
      selection.$from.parent.type.name === 'paragraph' &&
      selection.$from.parent.textContent === '';

    if (isEmptyParagraph) {
      try {
        const coords = view.coordsAtPos(from);
        setFloatPos({
          top:  coords.top - wrapRect.top + 1,
          left: -36,
        });
      } catch {
        setFloatPos(null);
      }
    } else {
      setFloatPos(null);
    }
  }, []);

  // Sync external content changes (e.g. when blog is loaded from Redux).
  // Skips sync when the change came from the editor itself (typing / formatting)
  // to prevent the cursor from being reset on every keystroke.
  const prevContentRef = useRef(content);
  useEffect(() => {
    if (!editor) return;

    // If the editor triggered this update, skip the sync and reset the flag
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      prevContentRef.current = content;
      return;
    }

    // Only sync when content actually changed externally (e.g. blog loaded)
    if (content === prevContentRef.current) return;
    prevContentRef.current = content;

    if (content) {
      editor.commands.setContent(content, false);
    } else {
      editor.commands.clearContent(false);
    }
  }, [content, editor]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    };
  }, []);

  // Cleanup preview URL when it changes
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    };
  }, [imagePreviewUrl]);

  // ── Link dialog flow ───────────────────────────────────────────────────────

  const handleOpenLinkDialog = useCallback(() => {
    if (!editor) return;

    // 1. Tell the toolbar that a sub-UI is engaged so blur timer won't hide it
    toolbarEngagedRef.current = true;
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);

    // 2. Snapshot the current ProseMirror selection so we can restore it after
    //    the Dialog steals focus from the editor.
    savedSelectionRef.current = editor.state.selection;

    // 3. Pre-fill with the existing href if cursor is on a link (for editing)
    const existingHref = editor.getAttributes('link').href ?? '';
    setLinkDefaultHref(existingHref);

    setLinkDialogOpen(true);
  }, [editor]);

  const handleLinkApply = useCallback(() => {
    if (!editor) return;
    const href = linkInputRef.current?.value?.trim() ?? '';

    // Restore snapshot selection and apply (or clear) the link
    editor
      .chain()
      .focus()
      .setTextSelection(savedSelectionRef.current)
      .run();

    if (href) {
      // Expand the mark range if the cursor is sitting inside an existing link
      // so the whole link word updates, not just the cursor position.
      editor
        .chain()
        .extendMarkRange('link')
        .setLink({ href, target: '_blank', rel: 'noopener noreferrer' })
        .run();
    } else {
      editor.chain().extendMarkRange('link').unsetLink().run();
    }

    setLinkDialogOpen(false);
    toolbarEngagedRef.current = false;
    savedSelectionRef.current = null;
  }, [editor]);

  const handleLinkRemove = useCallback(() => {
    if (!editor) return;
    editor
      .chain()
      .focus()
      .setTextSelection(savedSelectionRef.current)
      .extendMarkRange('link')
      .unsetLink()
      .run();

    setLinkDialogOpen(false);
    toolbarEngagedRef.current = false;
    savedSelectionRef.current = null;
  }, [editor]);

  const handleLinkDialogClose = useCallback(() => {
    setLinkDialogOpen(false);
    toolbarEngagedRef.current = false;
    savedSelectionRef.current = null;
    // Return focus to the editor so the user can keep typing
    editor?.commands.focus();
  }, [editor]);

  // ── Image insert flow ──────────────────────────────────────────────────────

  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setImagePendingFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const handleInsertImageConfirm = useCallback(async () => {
    if (!imagePendingFile || !editor) return;
    setImageUploading(true);
    beginUpload?.();
    try {
      const key = `blogSectionImage-${Date.now()}`;
      const uploadedUrl = await uploadToS3(
        key,
        imagePendingFile,
        DESTINATIONS.BLOG_DETAILS,
        false
      );
      if (uploadedUrl) {
        editor
          .chain()
          .focus()
          .setImage({ src: uploadedUrl, alt: imageAlt || '' })
          .run();
      }
    } catch (err) {
      console.error('Image upload failed:', err);
    } finally {
      setImageUploading(false);
      finishUpload?.();
      handleImagePickerClose();
    }
  }, [imagePendingFile, imageAlt, editor, uploadToS3, beginUpload, finishUpload]);

  const handleImagePickerClose = () => {
    setImagePickerOpen(false);
    if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    setImagePreviewUrl('');
    setImagePendingFile(null);
    setImageAlt('');
    if (imageFileRef.current) imageFileRef.current.value = '';
  };

  const handleOpenImagePicker = () => {
    setImagePickerOpen(true);
  };

  // ── Video embed flow ───────────────────────────────────────────────────────

  const handleOpenVideoDialog = (typeHint = null) => {
    setVideoTypeHint(typeHint);
    setVideoUrl('');
    setDetectedVideo(null);
    setVideoDialogOpen(true);
  };

  const handleVideoUrlChange = (e) => {
    const val = e.target.value;
    setVideoUrl(val);
    const detected = detectVideoFromUrl(val);
    setDetectedVideo(detected);
    if (detected) setVideoTypeHint(detected.videoType);
  };

  const handleInsertVideo = useCallback(() => {
    if (!editor || !detectedVideo) return;
    editor
      .chain()
      .focus()
      .insertContent({
        type: 'videoEmbed',
        attrs: {
          videoType: detectedVideo.videoType,
          videoId: detectedVideo.videoId,
          url: videoUrl.trim(),
        },
      })
      .run();
    setVideoDialogOpen(false);
    setVideoUrl('');
    setDetectedVideo(null);
    setVideoTypeHint(null);
  }, [editor, detectedVideo, videoUrl]);

  const handleVideoDialogClose = () => {
    setVideoDialogOpen(false);
    setVideoUrl('');
    setDetectedVideo(null);
    setVideoTypeHint(null);
  };

  if (!editor) return null;

  return (
    <div
      ref={editorWrapRef}
      className={styles.editorWrap}
      onClick={() => editor.commands.focus()}
      style={{ position: 'relative' }}
    >
      {/* ── Bubble Toolbar (text selection) ── */}
      {bubblePos && (
        <div
          className={styles.bubbleMenuWrapper}
          style={{
            position: 'absolute',
            top: Math.max(4, bubblePos.top),
            left: Math.max(0, bubblePos.left - 150),
            transform: 'translateX(-50%)',
            zIndex: 50,
            pointerEvents: 'auto',
          }}
          onMouseDown={(e) => e.preventDefault()} // prevent focus loss
        >
          <BubbleToolbar
            editor={editor}
            onOpenLinkDialog={handleOpenLinkDialog}
            onEngaged={(engaged) => {
              toolbarEngagedRef.current = engaged;
              // If disengaged and blur timer is not running, keep toolbar
              // visible based on whether there's still a selection.
              if (!engaged) {
                if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
                // Re-check selection state after popover closes
                blurTimerRef.current = setTimeout(() => {
                  const { empty } = editor.state.selection;
                  if (empty) {
                    setBubblePos(null);
                    setFloatPos(null);
                  }
                }, 100);
              }
            }}
          />
        </div>
      )}

      {/* ── Floating Insert Menu (empty paragraph) ── */}
      {floatPos && (
        <div
          style={{
            position: 'absolute',
            top: floatPos.top,
            left: floatPos.left,
            zIndex: 40,
            pointerEvents: 'auto',
          }}
          onMouseDown={(e) => e.preventDefault()} // prevent focus loss
        >
          <FloatingInsertMenu
            editor={editor}
            onInsertImage={handleOpenImagePicker}
            onInsertVideo={() => handleOpenVideoDialog()}
          />
        </div>
      )}

      {/* ── Editor content ── */}
      <EditorContent editor={editor} />

      {/* ── Link dialog — body-level portal, fully separate from editor focus ── */}
      <Dialog
        open={linkDialogOpen}
        onClose={handleLinkDialogClose}
        maxWidth="xs"
        fullWidth
        onClick={(e) => e.stopPropagation()}
        // Keep the Dialog on top of the bubble toolbar (z-index 50)
        sx={{ zIndex: 1400 }}
        // Re-focus the native input as soon as the Dialog mounts
        TransitionProps={{
          onEntered: () => {
            linkInputRef.current?.focus();
            // Place cursor at the end of any pre-filled URL
            const len = linkInputRef.current?.value?.length ?? 0;
            linkInputRef.current?.setSelectionRange(len, len);
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          Insert / Edit Link
          <IconButton size="small" onClick={handleLinkDialogClose}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          {/*
            Uncontrolled native <input> — value is read directly from the DOM via
            linkInputRef on Apply. No React state is involved while the user types,
            so there is zero batching delay and the field never loses focus.
          */}
          <input
            ref={linkInputRef}
            type="url"
            defaultValue={linkDefaultHref}
            placeholder="https://example.com"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleLinkApply();
              if (e.key === 'Escape') handleLinkDialogClose();
            }}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: '0.9rem',
              border: '1px solid #d5d7da',
              borderRadius: 6,
              outline: 'none',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
            }}
            onFocus={(e) =>
              (e.target.style.borderColor = '#0a6152')
            }
            onBlur={(e) =>
              (e.target.style.borderColor = '#d5d7da')
            }
          />
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          {/* Remove button — only shown when an existing link is being edited */}
          {linkDefaultHref && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={handleLinkRemove}
              sx={{ mr: 'auto' }}
            >
              Remove Link
            </Button>
          )}
          <Button variant="outlined" size="small" onClick={handleLinkDialogClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleLinkApply}
            sx={{ backgroundColor: '#0a6152', '&:hover': { backgroundColor: '#085044' } }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Image insert dialog ── */}
      <Dialog
        open={imagePickerOpen}
        onClose={handleImagePickerClose}
        maxWidth="sm"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Insert Image
          <IconButton size="small" onClick={handleImagePickerClose}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <input
            ref={imageFileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            style={{ marginBottom: 12 }}
            onChange={handleImageFileChange}
          />

          {imagePreviewUrl && (
            <div style={{ marginBottom: 12 }}>
              <img
                src={imagePreviewUrl}
                alt="Preview"
                style={{ maxWidth: '100%', borderRadius: 6, maxHeight: 260, objectFit: 'cover' }}
              />
            </div>
          )}

          <TextField
            size="small"
            fullWidth
            label="Alt text (optional)"
            value={imageAlt}
            onChange={(e) => setImageAlt(e.target.value)}
            placeholder="Describe the image…"
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={handleImagePickerClose} disabled={imageUploading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleInsertImageConfirm}
            disabled={!imagePendingFile || imageUploading}
            startIcon={imageUploading ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ backgroundColor: '#0a6152', '&:hover': { backgroundColor: '#085044' } }}
          >
            {imageUploading ? 'Uploading…' : 'Upload & Insert'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Video embed dialog ── */}
      <Dialog
        open={videoDialogOpen}
        onClose={handleVideoDialogClose}
        maxWidth="sm"
        fullWidth
        onClick={(e) => e.stopPropagation()}
        sx={{ zIndex: 1400 }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          Embed Video
          <IconButton size="small" onClick={handleVideoDialogClose}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          {/* Type selector */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <button
              type="button"
              onClick={() => setVideoTypeHint('youtube')}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '10px 0',
                borderRadius: 8,
                border: `2px solid ${videoTypeHint === 'youtube' ? '#FF0000' : '#d5d7da'}`,
                background: videoTypeHint === 'youtube' ? '#fff5f5' : '#fff',
                color: videoTypeHint === 'youtube' ? '#FF0000' : '#64748b',
                fontWeight: 600,
                fontSize: '0.875rem',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <YouTube style={{ fontSize: 22, color: '#FF0000' }} />
              YouTube Video
            </button>
            <button
              type="button"
              onClick={() => setVideoTypeHint('instagram')}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '10px 0',
                borderRadius: 8,
                border: `2px solid ${videoTypeHint === 'instagram' ? '#E1306C' : '#d5d7da'}`,
                background: videoTypeHint === 'instagram' ? '#fff0f5' : '#fff',
                color: videoTypeHint === 'instagram' ? '#E1306C' : '#64748b',
                fontWeight: 600,
                fontSize: '0.875rem',
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <Instagram style={{ fontSize: 22, color: '#E1306C' }} />
              Instagram Reel
            </button>
          </div>

          {/* URL input */}
          <input
            type="url"
            value={videoUrl}
            onChange={handleVideoUrlChange}
            placeholder={
              videoTypeHint === 'instagram'
                ? 'https://www.instagram.com/reel/…'
                : 'https://www.youtube.com/watch?v=… or https://youtu.be/…'
            }
            onKeyDown={(e) => { if (e.key === 'Enter' && detectedVideo) handleInsertVideo(); }}
            style={{
              width: '100%',
              padding: '9px 12px',
              fontSize: '0.9rem',
              border: `1px solid ${detectedVideo ? '#0a6152' : '#d5d7da'}`,
              borderRadius: 6,
              outline: 'none',
              boxSizing: 'border-box',
              fontFamily: 'inherit',
              transition: 'border-color 0.15s',
            }}
          />

          {/* Detection feedback */}
          {videoUrl && (
            <div style={{ marginTop: 10, fontSize: '0.8rem', color: detectedVideo ? '#0a6152' : '#dc2626' }}>
              {detectedVideo
                ? `✓ Detected ${detectedVideo.videoType === 'youtube' ? 'YouTube' : 'Instagram Reel'} · ID: ${detectedVideo.videoId}`
                : 'Could not detect a YouTube or Instagram URL. Please check the link.'}
            </div>
          )}

          {/* Inline preview */}
          {detectedVideo && detectedVideo.videoType === 'instagram' ? (
            <div
              style={{
                marginTop: 14,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 10,
                background: 'linear-gradient(160deg,#833ab4 0%,#fd1d1d 50%,#fcb045 100%)',
                borderRadius: 10,
                padding: '18px 16px',
              }}
            >
              <div
                style={{
                  width: 200,
                  aspectRatio: '9/16',
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: '#000',
                  boxShadow: '0 6px 24px rgba(0,0,0,0.4)',
                  flexShrink: 0,
                }}
              >
                <iframe
                  src={getEmbedSrc(detectedVideo.videoType, detectedVideo.videoId)}
                  title="Instagram Reel preview"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                />
              </div>
              <span style={{ color: '#fff', fontSize: 11, fontWeight: 600, opacity: 0.9 }}>
                📷 Instagram Reel preview
              </span>
            </div>
          ) : detectedVideo ? (
            <div
              style={{
                marginTop: 14,
                position: 'relative',
                paddingBottom: '56.25%',
                height: 0,
                overflow: 'hidden',
                borderRadius: 8,
                background: '#0f172a',
              }}
            >
              <iframe
                src={getEmbedSrc(detectedVideo.videoType, detectedVideo.videoId)}
                title="YouTube video preview"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: 8,
                }}
                allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </div>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button variant="outlined" size="small" onClick={handleVideoDialogClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleInsertVideo}
            disabled={!detectedVideo}
            sx={{ backgroundColor: '#0a6152', '&:hover': { backgroundColor: '#085044' } }}
          >
            Embed Video
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
